import { Box, IconButton, useTheme } from "@mui/material";
import SidebarHeader from "./sidebar-header";
import CustomerInformationSection from "./sections/customer-information-section";
import CampaignSection from "./sections/campaign-section";
import ArchivedOverlay from "@/modules/common/components/ui-kit/archived-overlay";
import { useContext, useMemo } from "react";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { TelephoneProvider } from "../../context/telephone.context";
import EditTelephone from "./fields/telephone";
import {
  EmailField,
  LastContactField,
  LastRequestField,
  LeadIdField,
  MethodOfContactField,
  CustomrIdField,
} from "./fields";
import { PartnerInfo } from "./fields/partner-info";

import LeadLocation from "./fields/linked-location/linked-location";
import LeadProfile from "./lead-profile";
import { SquareArrowOutUpRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Calendar,
  Mail,
  MapPin,
  MessageSquare,
  User,
} from "lucide-react";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import { userHasPermission } from "@/modules/common/utils";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";

const LeadSidebar = ({ showActions = true }: { showActions?: boolean }) => {
  const theme = useTheme();
  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);

  const hideCampaignColumns = userHasPermission(
    {
      name: PermissionsEnum.HIDE_CAMPAIGN_COLUMNS,
      level: PermissionLevel.READ,
    },
    currentUserPermissions
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { lead } = useContext(LeadPageContext);
  const isArchived = lead?.data?.lead?.archived;

  const doNotCall = lead?.data?.lead?.do_not_call;
  const leadMethodOfContact = lead?.data?.lead.method_of_contact?.name;
  const nanoId = lead?.data?.nanoId;
  const isPropertyTracksRoute = location.pathname.includes("property-tracks");

  const showPartnerInfo =
    leadMethodOfContact?.includes("ambassador app") ||
    leadMethodOfContact?.includes("broker app");

  const sectionFields = useMemo(
    () => [
      ...(!doNotCall
        ? [
            {
              title: "Contact",
              field: (
                <TelephoneProvider>
                  <EditTelephone />
                </TelephoneProvider>
              ),
              icon: (
                <MessageSquare
                  width={"18px"}
                  height={"18px"}
                  color={theme.nawy.colors.primitives.grey[900]}
                />
              ),
            },
          ]
        : []),

      {
        title: "Method Of Contact",
        field: <MethodOfContactField />,
        icon: (
          <MessageSquare
            width={"18px"}
            height={"18px"}
            color={theme.nawy.colors.primitives.grey[900]}
          />
        ),
      },
      ...(showPartnerInfo
        ? [
            {
              title: "",
              field: <PartnerInfo />,
            },
          ]
        : []),
      {
        title: "Lead ID",
        field: <LeadIdField />,
        titleAction:
          nanoId && isPropertyTracksRoute ? (
            <IconButton
              size="small"
              onClick={() => navigate(`/leads-new/${nanoId}`)}
              sx={{
                padding: "2px",
                color: theme.nawy.colors.semantic.text.accent,
                "&:hover": {
                  backgroundColor: theme.nawy.colors.semantic.layer.secondary,
                },
              }}
            >
              <SquareArrowOutUpRight size={14} />
            </IconButton>
          ) : undefined,
        icon: (
          <User
            width={"18px"}
            height={"18px"}
            color={theme.nawy.colors.primitives.grey[900]}
          />
        ),
      },
      {
        title: "Customer ID",
        field: <CustomrIdField />,
        icon: (
          <User
            width={"18px"}
            height={"18px"}
            color={theme.nawy.colors.primitives.grey[900]}
          />
        ),
      },
      {
        title: "Email",
        field: <EmailField />,
        icon: (
          <Mail
            width={"18px"}
            height={"18px"}
            color={theme.nawy.colors.primitives.grey[900]}
          />
        ),
      },

      {
        title: "Last Request",
        field: <LastRequestField />,
        icon: (
          <Calendar
            width={"18px"}
            height={"18px"}
            color={theme.nawy.colors.primitives.grey[900]}
          />
        ),
      },
      {
        title: "Last Contact",
        field: <LastContactField />,
        icon: (
          <Calendar
            width={"18px"}
            height={"18px"}
            color={theme.nawy.colors.primitives.grey[900]}
          />
        ),
      },

      {
        title: "Location",
        field: <LeadLocation />,
        icon: (
          <MapPin
            width={"18px"}
            height={"18px"}
            color={theme.nawy.colors.primitives.grey[900]}
          />
        ),
      },
    ],
    [
      showPartnerInfo,
      doNotCall,
      nanoId,
      isPropertyTracksRoute,
      navigate,
      theme.nawy.colors.semantic.text.accent,
      theme.nawy.colors.semantic.layer.secondary,
    ]
  );

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: "260px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderLeft: "1px solid",
        borderLeftColor: "divider",
        borderTopLeftRadius: theme.nawy.radius.md,
        overflow: "hidden", // Prevent overflow from parent container
      }}
    >
      {/* Header with back button and profile */}
      <SidebarHeader showActions={showActions}>
        <LeadProfile />
      </SidebarHeader>

      {/* Accordion sections */}
      <Box
        sx={{
          width: "100%",
          flex: 1, // Take remaining space
          border: "1px solid",
          borderColor: "divider",
          borderLeft: "none",
          borderTop: "none",
          overflowY: "auto", // Enable vertical scrolling
          overflowX: "hidden", // Hide horizontal scroll
        }}
      >
        <ArchivedOverlay isArchived={isArchived}>
          <CustomerInformationSection fields={sectionFields} />
          {!hideCampaignColumns && <CampaignSection />}
        </ArchivedOverlay>
      </Box>
    </Box>
  );
};

export default LeadSidebar;
